import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface MeetingType {
  id: 'physical' | 'virtual' | 'hybrid';
  name: string;
  icon: string;
  description: string;
}

interface DurationOption {
  value: string;
  label: string;
}

const CreateMeeting: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const navigate = useNavigate();
  const colors = getThemeColors('dark');

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    meetingDate: '',
    meetingTime: '',
    scheduledAt: '', // Will be calculated from date + time
    duration: '60',
    location: '',
    meetingUrl: '',
    meetingType: 'physical',
    attendeeEmails: '',
    addToCalendar: true,
    calendarId: 'primary',
  });

  // Calculate end time based on start time and duration
  const calculateEndTime = (date: string, time: string, durationMinutes: number) => {
    if (!date || !time || !durationMinutes) return '';

    try {
      const startDateTime = new Date(`${date}T${time}:00+03:00`);
      const endDateTime = new Date(startDateTime.getTime() + (durationMinutes * 60 * 1000));

      return endDateTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Africa/Nairobi'
      });
    } catch (error) {
      return '';
    }
  };

  // Get calculated end time
  const endTime = calculateEndTime(formData.meetingDate, formData.meetingTime, parseInt(formData.duration));

  // Error state for form validation
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showErrors, setShowErrors] = useState(false);

  // Ref for scrolling to errors
  const scrollViewRef = useRef<HTMLDivElement>(null);

  const meetingTypes: MeetingType[] = [
    {
      id: 'physical',
      name: 'Physical Meeting',
      icon: 'location',
      description: 'In-person meeting at a physical location'
    },
    {
      id: 'virtual',
      name: 'Virtual Meeting',
      icon: 'videocam',
      description: 'Online meeting via video call platform'
    },
    {
      id: 'hybrid',
      name: 'Hybrid Meeting',
      icon: 'people',
      description: 'Both physical and virtual attendance options'
    },
  ];

  const durations: DurationOption[] = [
    { value: '30', label: '30 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' },
    { value: '180', label: '3 hours' },
  ];

  const handleInputChange = (field: string, value: string) => {
    // Basic malicious input filtering
    let sanitizedValue = value;
    if (typeof value === 'string') {
      // Remove potentially malicious characters
      sanitizedValue = value.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                           .replace(/javascript:/gi, '')
                           .replace(/on\w+\s*=/gi, '');
    }

    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: sanitizedValue
      };

      // Auto-calculate scheduledAt when date or time changes
      if (field === 'meetingDate' || field === 'meetingTime') {
        const date = field === 'meetingDate' ? sanitizedValue : prev.meetingDate;
        const time = field === 'meetingTime' ? sanitizedValue : prev.meetingTime;

        if (date && time) {
          // Validate date format before combining
          const dateObj = new Date(date);
          if (!isNaN(dateObj.getTime())) {
            // Combine date and time into RFC3339 format with EAT timezone
            newData.scheduledAt = `${date}T${time}:00+03:00`;
            console.log('🔍 Auto-calculated scheduledAt:', newData.scheduledAt);
          } else {
            console.error('❌ Invalid date format:', date);
            newData.scheduledAt = '';
          }
        } else {
          newData.scheduledAt = '';
        }
      }

      return newData;
    });

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Validate required fields
    if (!formData.title.trim()) {
      newErrors.title = 'Please enter a meeting title';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Meeting title must be at least 3 characters long';
    } else if (formData.title.trim().length > 100) {
      newErrors.title = 'Meeting title must be less than 100 characters';
    }

    // Validate description length if provided
    if (formData.description.trim() && formData.description.trim().length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    // Validate meeting date
    if (!formData.meetingDate.trim()) {
      newErrors.meetingDate = 'Please select the meeting date';
    } else {
      // Check if date string is in valid format (YYYY-MM-DD)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.meetingDate.trim())) {
        newErrors.meetingDate = 'Please enter a valid date in YYYY-MM-DD format';
      } else {
        try {
          const testDate = new Date(formData.meetingDate + 'T00:00:00+03:00');
          if (isNaN(testDate.getTime())) {
            newErrors.meetingDate = 'Please enter a valid date';
          } else {
            // Check if date is not in the past (allow today)
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (testDate < today) {
              newErrors.meetingDate = 'Meeting date must be today or in the future';
            }
          }
        } catch (error) {
          newErrors.meetingDate = 'Please enter a valid date';
        }
      }
    }

    // Validate meeting time
    if (!formData.meetingTime.trim()) {
      newErrors.meetingTime = 'Please select the meeting time';
    } else {
      // Validate time format (HH:MM)
      const timePattern = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timePattern.test(formData.meetingTime.trim())) {
        newErrors.meetingTime = 'Please enter a valid time in HH:MM format';
      }
    }

    // Validate combined date and time is in the future
    if (formData.meetingDate && formData.meetingTime && !newErrors.meetingDate && !newErrors.meetingTime) {
      try {
        const combinedDateTime = new Date(`${formData.meetingDate}T${formData.meetingTime}:00+03:00`);
        if (combinedDateTime < new Date()) {
          newErrors.meetingTime = 'Meeting time must be in the future';
        }
      } catch (error) {
        newErrors.meetingTime = 'Invalid date and time combination';
      }
    }

    // Validate location for physical meetings
    if (formData.meetingType === 'physical' && !formData.location.trim()) {
      newErrors.location = 'Please enter a physical location for the meeting venue';
    }

    // Validate meeting URL for virtual meetings
    if (formData.meetingType === 'virtual' && !formData.meetingUrl.trim()) {
      newErrors.meetingUrl = 'Please enter a meeting URL for the virtual meeting';
    } else if (formData.meetingType === 'virtual' && formData.meetingUrl.trim()) {
      // Basic URL validation
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      if (!urlPattern.test(formData.meetingUrl.trim())) {
        newErrors.meetingUrl = 'Please enter a valid URL (e.g., https://zoom.us/j/123456789)';
      }
    }

    // Validate both location and URL for hybrid meetings
    if (formData.meetingType === 'hybrid') {
      if (!formData.location.trim()) {
        newErrors.location = 'Please enter a physical location for hybrid meeting';
      }
      if (!formData.meetingUrl.trim()) {
        newErrors.meetingUrl = 'Please enter a meeting URL for hybrid meeting';
      } else {
        // Basic URL validation for hybrid meetings
        const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
        if (!urlPattern.test(formData.meetingUrl.trim())) {
          newErrors.meetingUrl = 'Please enter a valid URL (e.g., https://zoom.us/j/123456789)';
        }
      }
    }

    // Validate attendee emails if provided
    if (formData.attendeeEmails.trim()) {
      const emails = formData.attendeeEmails.split(',').map(email => email.trim());
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = [];

      for (const email of emails) {
        if (email && !emailRegex.test(email)) {
          invalidEmails.push(email);
        }
      }

      if (invalidEmails.length > 0) {
        newErrors.attendeeEmails = `Invalid email format: ${invalidEmails.join(', ')}`;
      }
    }

    setErrors(newErrors);
    setShowErrors(true);

    // Scroll to top if there are errors to show the general error message
    if (Object.keys(newErrors).length > 0 && scrollViewRef.current) {
      setTimeout(() => {
        scrollViewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    console.log('🔄 Meeting form submitted!');
    console.log('📋 Form data:', formData);

    if (!validateForm()) {
      console.log('❌ Form validation failed');
      return;
    }

    console.log('✅ Form validation passed');

    try {
      setLoading(true);
      console.log('🔄 Setting loading to true');

      // Use the scheduledAt that was already calculated and validated
      let scheduledAtRFC3339 = formData.scheduledAt.trim();

      console.log('📅 Using pre-calculated scheduledAt:', scheduledAtRFC3339);

      // Final verification that the date is valid
      const testDate = new Date(scheduledAtRFC3339);
      if (isNaN(testDate.getTime())) {
        console.error('❌ Invalid date format:', scheduledAtRFC3339);
        alert('Invalid date format. Please check your date and time inputs.');
        return;
      }

      console.log('📅 Parsed date object:', testDate);
      console.log('📅 ISO string:', testDate.toISOString());
      console.log('📅 Will display as:', new Date(testDate).toLocaleString());

      // Parse attendee emails
      const attendeeEmails = formData.attendeeEmails.trim()
        ? formData.attendeeEmails.split(',').map(email => email.trim()).filter(email => email)
        : [];

      const meetingData = {
        chamaId,
        title: formData.title.trim(),
        description: formData.description.trim(),
        scheduledAt: scheduledAtRFC3339,
        duration: parseInt(formData.duration),
        location: formData.location.trim(),
        meetingUrl: formData.meetingUrl.trim(),
        meetingType: formData.meetingType, // Use meetingType for new API
        recordingEnabled: formData.meetingType === 'virtual' || formData.meetingType === 'hybrid', // Enable recording for virtual meetings
        attendeeEmails: attendeeEmails,
        calendarId: formData.addToCalendar ? formData.calendarId : null,
      };

      console.log('📤 Sending meeting data:', meetingData);
      console.log('🔗 API call starting...');

      // Choose the appropriate endpoint based on meeting type and features
      let endpoint = API_ENDPOINTS.MEETINGS;

      if (formData.addToCalendar && attendeeEmails.length > 0) {
        // Use calendar integration endpoint for meetings with calendar invites
        endpoint = API_ENDPOINTS.MEETINGS_CALENDAR;
      }
      // All meeting types use the same endpoint - the backend handles the differences

      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ No auth token found');
        alert('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(meetingData),
      });

      console.log('📥 API response received:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('🎉 Meeting created successfully!', data);

        // Navigate back to meetings list
        navigate(`/dashboard/chama/${chamaId}/meetings`);
      } else {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.log('❌ Meeting creation failed:', response.status, errorText);
        alert(`Failed to schedule meeting: ${errorText}`);
      }
    } catch (error) {
      console.error('❌ Error creating meeting:', error);
      alert('Failed to schedule meeting. Please try again.');
    } finally {
      console.log('🔄 Setting loading to false');
      setLoading(false);
    }
  };

  const renderMeetingTypeOption = (type: MeetingType) => (
    <div
      key={type.id}
      style={{
        ...styles.typeOption,
        backgroundColor: formData.meetingType === type.id ? colors.primary + '20' : colors.surface,
        border: `2px solid ${formData.meetingType === type.id ? colors.primary : colors.border}`,
        cursor: 'pointer',
      }}
      onClick={() => handleInputChange('meetingType', type.id)}
    >
      <div style={styles.typeHeader}>
        <span style={{ fontSize: '24px', marginRight: '8px' }}>
          {type.icon === 'location' ? '📍' : type.icon === 'videocam' ? '📹' : '👥'}
        </span>
        <div style={{ ...styles.typeName, color: formData.meetingType === type.id ? colors.primary : colors.text }}>
          {type.name}
        </div>
        {formData.meetingType === type.id && (
          <span style={{ fontSize: '20px', color: colors.primary }}>✓</span>
        )}
      </div>
      <div style={{
        ...styles.typeDescription,
        color: formData.meetingType === type.id ? colors.primary : colors.textSecondary
      }}>
        {type.description}
      </div>
    </div>
  );

  const renderDurationOption = (duration: DurationOption) => (
    <div
      key={duration.value}
      style={{
        ...styles.durationOption,
        backgroundColor: formData.duration === duration.value ? colors.primary + '20' : colors.surface,
        border: `2px solid ${formData.duration === duration.value ? colors.primary : colors.border}`,
        cursor: 'pointer',
      }}
      onClick={() => handleInputChange('duration', duration.value)}
    >
      <div style={{
        ...styles.durationText,
        color: formData.duration === duration.value ? colors.primary : colors.text
      }}>
        {duration.label}
      </div>
      {formData.duration === duration.value && (
        <span style={{ fontSize: '16px', color: colors.primary }}>✓</span>
      )}
    </div>
  );

  // Error message component
  const ErrorMessage = ({ error }: { error?: string }) => {
    if (!error || !showErrors) return null;

    return (
      <div style={styles.errorContainer}>
        <span style={{ color: colors.error }}>⚠️</span>
        <span style={{ ...styles.errorText, color: colors.error }}>
          {error}
        </span>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Schedule Meeting"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent} ref={scrollViewRef}>
        <div style={styles.content}>
          {/* General error message */}
          {showErrors && Object.keys(errors).length > 0 && (
            <div style={{
              ...styles.generalErrorContainer,
              backgroundColor: colors.error + '20',
              border: `1px solid ${colors.error}`
            }}>
              <span style={{ fontSize: '20px', color: colors.error }}>⚠️</span>
              <div style={styles.generalErrorContent}>
                <div style={{ ...styles.generalErrorTitle, color: colors.error }}>
                  Please fix the following errors:
                </div>
                <div style={{ ...styles.generalErrorText, color: colors.error }}>
                  {Object.keys(errors).length} field{Object.keys(errors).length > 1 ? 's' : ''} need{Object.keys(errors).length === 1 ? 's' : ''} attention
                </div>
              </div>
            </div>
          )}

          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Meeting Details</h3>

            <div style={styles.formGroup}>
              <label style={{ ...styles.label, color: colors.text }}>Meeting Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter meeting title"
                style={{
                  ...styles.input,
                  backgroundColor: colors.surface,
                  color: colors.text,
                  border: `1px solid ${errors.title && showErrors ? colors.error : colors.border}`
                }}
              />
              <ErrorMessage error={errors.title} />
            </div>

            <div style={styles.formGroup}>
              <label style={{ ...styles.label, color: colors.text }}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Meeting agenda and details..."
                rows={3}
                style={{
                  ...styles.textarea,
                  backgroundColor: colors.surface,
                  color: colors.text,
                  border: `1px solid ${errors.description && showErrors ? colors.error : colors.border}`
                }}
              />
              <ErrorMessage error={errors.description} />
            </div>

            {/* Meeting Date */}
            <div style={styles.formGroup}>
              <label style={{ ...styles.label, color: colors.text }}>Meeting Date *</label>
              <input
                type="date"
                value={formData.meetingDate}
                onChange={(e) => handleInputChange('meetingDate', e.target.value)}
                style={{
                  ...styles.input,
                  backgroundColor: colors.surface,
                  color: colors.text,
                  border: `1px solid ${errors.meetingDate && showErrors ? colors.error : colors.border}`
                }}
              />
              <ErrorMessage error={errors.meetingDate} />
            </div>

            {/* Meeting Time */}
            <div style={styles.formGroup}>
              <label style={{ ...styles.label, color: colors.text }}>Meeting Time *</label>
              <input
                type="time"
                value={formData.meetingTime}
                onChange={(e) => handleInputChange('meetingTime', e.target.value)}
                style={{
                  ...styles.input,
                  backgroundColor: colors.surface,
                  color: colors.text,
                  border: `1px solid ${errors.meetingTime && showErrors ? colors.error : colors.border}`
                }}
              />
              <ErrorMessage error={errors.meetingTime} />
            </div>

            {/* End Time Display */}
            {endTime && (
              <div style={{
                ...styles.endTimeContainer,
                backgroundColor: colors.primary + '20',
                border: `1px solid ${colors.primary}`
              }}>
                <span style={{ fontSize: '16px', color: colors.primary }}>⏰</span>
                <span style={{ ...styles.endTimeText, color: colors.primary }}>
                  Meeting will end at: {endTime} EAT
                </span>
              </div>
            )}
          </div>

          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Meeting Type</h3>

            <div style={styles.typeGrid}>
              {meetingTypes.map(renderMeetingTypeOption)}
            </div>
          </div>

          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Duration</h3>

            <div style={styles.durationGrid}>
              {durations.map(renderDurationOption)}
            </div>
          </div>

          {formData.meetingType === 'physical' && (
            <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Physical Meeting Location</h3>

              <div style={styles.formGroup}>
                <label style={{ ...styles.label, color: colors.text }}>Meeting Venue *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="Enter physical meeting venue/address"
                  style={{
                    ...styles.input,
                    backgroundColor: colors.surface,
                    color: colors.text,
                    border: `1px solid ${errors.location && showErrors ? colors.error : colors.border}`
                  }}
                />
                <ErrorMessage error={errors.location} />
              </div>
            </div>
          )}

          {formData.meetingType === 'virtual' && (
            <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Virtual Meeting Details</h3>

              <div style={styles.formGroup}>
                <label style={{ ...styles.label, color: colors.text }}>Meeting URL *</label>
                <input
                  type="url"
                  value={formData.meetingUrl}
                  onChange={(e) => handleInputChange('meetingUrl', e.target.value)}
                  placeholder="Zoom, Teams, Google Meet, or other video call link"
                  style={{
                    ...styles.input,
                    backgroundColor: colors.surface,
                    color: colors.text,
                    border: `1px solid ${errors.meetingUrl && showErrors ? colors.error : colors.border}`
                  }}
                />
                <ErrorMessage error={errors.meetingUrl} />
              </div>
            </div>
          )}

          {formData.meetingType === 'hybrid' && (
            <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Hybrid Meeting Details</h3>

              <div style={styles.formGroup}>
                <label style={{ ...styles.label, color: colors.text }}>Physical Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="Enter physical meeting venue/address"
                  style={{
                    ...styles.input,
                    backgroundColor: colors.surface,
                    color: colors.text,
                    border: `1px solid ${errors.location && showErrors ? colors.error : colors.border}`
                  }}
                />
                <ErrorMessage error={errors.location} />
              </div>

              <div style={styles.formGroup}>
                <label style={{ ...styles.label, color: colors.text }}>Virtual Meeting URL *</label>
                <input
                  type="url"
                  value={formData.meetingUrl}
                  onChange={(e) => handleInputChange('meetingUrl', e.target.value)}
                  placeholder="Zoom, Teams, Google Meet, or other video call link"
                  style={{
                    ...styles.input,
                    backgroundColor: colors.surface,
                    color: colors.text,
                    border: `1px solid ${errors.meetingUrl && showErrors ? colors.error : colors.border}`
                  }}
                />
                <ErrorMessage error={errors.meetingUrl} />
              </div>
            </div>
          )}

          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.sectionTitle, color: colors.text }}>Attendees & Calendar Integration</h3>

            <div style={styles.formGroup}>
              <label style={{ ...styles.label, color: colors.text }}>Attendee Emails (optional)</label>
              <textarea
                value={formData.attendeeEmails}
                onChange={(e) => handleInputChange('attendeeEmails', e.target.value)}
                placeholder="email1@example.com, email2@example.com"
                rows={2}
                style={{
                  ...styles.textarea,
                  backgroundColor: colors.surface,
                  color: colors.text,
                  border: `1px solid ${errors.attendeeEmails && showErrors ? colors.error : colors.border}`
                }}
              />
              <ErrorMessage error={errors.attendeeEmails} />
            </div>

            <div
              style={{
                ...styles.calendarOption,
                backgroundColor: formData.addToCalendar ? colors.primary + '20' : colors.surface,
                border: `2px solid ${formData.addToCalendar ? colors.primary : colors.border}`,
                cursor: 'pointer',
              }}
              onClick={() => handleInputChange('addToCalendar', (!formData.addToCalendar).toString())}
            >
              <div style={styles.calendarHeader}>
                <span style={{ fontSize: '24px', marginRight: '8px' }}>📅</span>
                <div style={{ ...styles.calendarText, color: formData.addToCalendar ? colors.primary : colors.text }}>
                  Add to Google Calendar
                </div>
                <span style={{
                  fontSize: '20px',
                  color: formData.addToCalendar ? colors.primary : colors.textSecondary
                }}>
                  {formData.addToCalendar ? '✓' : '○'}
                </span>
              </div>
              <div style={{
                ...styles.calendarDescription,
                color: formData.addToCalendar ? colors.primary : colors.textSecondary
              }}>
                Automatically create calendar event and send invites to attendees
              </div>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              ...styles.submitButton,
              backgroundColor: loading ? colors.textSecondary : colors.primary,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Scheduling...' : 'Schedule Meeting'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  card: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    marginBottom: spacing.md,
    ...getShadowStyle('sm'),
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    margin: 0,
    marginBottom: spacing.md,
  },
  formGroup: {
    marginBottom: spacing.lg,
  },
  label: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
    display: 'block',
  },
  input: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    outline: 'none',
  },
  textarea: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.base,
    outline: 'none',
    resize: 'vertical',
    minHeight: 80,
  },
  typeGrid: {
    gap: spacing.sm,
  },
  typeOption: {
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    borderWidth: 1,
  },
  typeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  typeName: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  typeDescription: {
    fontSize: typography.fontSize.sm,
  },
  durationGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: spacing.sm,
  },
  durationOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: spacing.md,
    paddingRight: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    borderRadius: borderRadius.md,
    borderWidth: 1,
  },
  durationText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  submitButton: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.md,
  },
  calendarOption: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    borderWidth: 1,
    marginTop: spacing.sm,
  },
  calendarHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  calendarText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
  },
  calendarDescription: {
    fontSize: typography.fontSize.sm,
    marginLeft: 32,
  },
  // Error styles
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: spacing.xs,
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    marginLeft: spacing.xs,
    flex: 1,
  },
  // General error container styles
  generalErrorContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
    flexDirection: 'row',
  },
  generalErrorContent: {
    flex: 1,
    marginLeft: spacing.sm,
  },
  generalErrorTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing.xs,
  },
  generalErrorText: {
    fontSize: typography.fontSize.sm,
  },
  endTimeContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: spacing.sm,
    borderRadius: borderRadius.md,
    marginTop: spacing.sm,
  },
  endTimeText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    marginLeft: spacing.xs,
  },
};

export default CreateMeeting;